import type React from 'react';

import { Button } from '@change/ds-core-ui/Button';
import { FocusRing } from '@change/ds-core-ui/util';
import { ArrowLeftIcon } from '@change/ds-icons/ArrowLeftIcon';
import { ArrowRightIcon } from '@change/ds-icons/ArrowRightIcon';
import { cn } from '@change/ds-utils';

import { useI18n } from '@change-corgi/core-react/i18n';

import type { VideoStory } from '#src/app/pages/about/impact/pageContext';

import { VideoSlide } from './components/VideoSlide';
import { useVideoCarousel } from './hooks/useVideoCarousel';

type Props = Readonly<{ stories: readonly VideoStory[] }>;

export const VideoCarousel: React.FC<Props> = ({ stories }) => {
	const { translate } = useI18n();
	const {
		data: { activeIndex },
		actions: { next, prev, goTo, onPointerDown, onPointerUp },
	} = useVideoCarousel(stories);

	return (
		<div className="flex flex-col gap-6">
			<div className="overflow-hidden">
				{/* --slide and --gap are set per breakpoint (88%/3% on mobile, 76%/3% on desktop, capped at the
				    912px/36px those come to on the 1200px content width), and one offset centres the active slide
				    with its neighbours peeking, straight from CSS so no JS media query is needed. */}
				<div
					onPointerDown={onPointerDown}
					onPointerUp={onPointerUp}
					className="duration-t5 ease-inform flex translate-x-[calc((100%-var(--slide))/2-var(--active-index)*(var(--slide)+var(--gap)))] touch-pan-y gap-(--gap) transition-transform [--gap:3%] [--slide:88%] motion-reduce:transition-none md:[--gap:min(3%,36px)] md:[--slide:min(76%,912px)]"
					style={{ '--active-index': activeIndex } as React.CSSProperties}
					data-qa="impact-video-carousel-track"
				>
					{stories.map((story, index) => {
						const isActive = index === activeIndex;
						return (
							// pointer-only convenience, as in the design: keyboard users reach every slide through the
							// arrows and dots, and only the active slide's player is in the focus order
							<div
								key={story.id}
								role="presentation"
								data-active={isActive}
								onClick={() => goTo(index)}
								className={cn(
									'duration-t4 shrink-0 basis-(--slide) transition-opacity',
									isActive ? 'opacity-100' : 'opacity-40',
								)}
							>
								<VideoSlide story={story} isActive={isActive} />
							</div>
						);
					})}
				</div>
			</div>
			<div className="flex items-center justify-center gap-4">
				<Button
					iconOnly
					variant="secondary"
					size="large"
					leadingIcon={<ArrowLeftIcon />}
					aria-label={translate('corgi.impact_page.petition_starters.previous_video_label')}
					onClick={prev}
					className="rounded-full before:rounded-full"
					data-qa="impact-video-carousel-prev"
				/>
				<div role="tablist" className="flex items-center gap-2">
					{stories.map((story, index) => (
						<FocusRing key={story.id} focusRingClass="rounded-full">
							<button
								type="button"
								role="tab"
								aria-selected={index === activeIndex}
								aria-label={translate('corgi.impact_page.petition_starters.go_to_video_label', {
									index: index + 1,
									total: stories.length,
								})}
								onClick={() => goTo(index)}
								className={cn(
									'duration-t4 h-2 rounded-full transition-all',
									index === activeIndex ? 'bg-typography-strong w-6' : 'bg-fill-weaker w-2',
								)}
							/>
						</FocusRing>
					))}
				</div>
				<Button
					iconOnly
					variant="secondary"
					size="large"
					leadingIcon={<ArrowRightIcon />}
					aria-label={translate('corgi.impact_page.petition_starters.next_video_label')}
					onClick={next}
					className="rounded-full before:rounded-full"
					data-qa="impact-video-carousel-next"
				/>
			</div>
		</div>
	);
};
